import { Link } from "react-router-dom";
import { ArrowRight, DoorOpen, Heart, HandHeart, Coffee, Sun, MessagesSquare } from "lucide-react";
import { MaskedLine, FadeUp, ParallaxImage, Page, Meta } from "../components/Reveal";
import { useContent } from "../lib/ContentContext";

const ARCH = "rounded-t-[999px] rounded-b-[2rem]";

const PILLAR_ICONS = [DoorOpen, Heart, HandHeart, Coffee, Sun, MessagesSquare];

export default function Eugenie() {
  const c = useContent();

  const CHAPTERS = [1, 2, 3, 4].map((n) => ({
    n: String(n).padStart(2, "0"),
    title: c(`eugenie.chapitre.${n}.titre`),
    text: c(`eugenie.chapitre.${n}.texte`),
  }));

  const FOUNDATIONS = [1, 2, 3].map((n) => ({
    label: c(`eugenie.fondation.${n}.label`),
    text: c(`eugenie.fondation.${n}.texte`),
  }));

  const PILLARS = PILLAR_ICONS.map((Icon, i) => ({
    Icon,
    title: c(`eugenie.pilier.${i + 1}.titre`),
    text: c(`eugenie.pilier.${i + 1}.texte`),
  }));

  const PARCOURS = [1, 2, 3, 4].map((n) => ({
    period: c(`eugenie.etape.${n}.periode`),
    text: c(`eugenie.etape.${n}.texte`),
  }));

  const DIPLOMES = [1, 2, 3].map((n) => c(`eugenie.diplome.${n}`));

  // Formation continue : une ligne « ANNÉE :: texte » par formation (éditable dans l'admin).
  const FORMATION_GROUPS = [];
  c("eugenie.formations.liste").split("\n").map((l) => l.trim()).filter(Boolean).forEach((l) => {
    const sep = l.indexOf("::");
    const annee = sep > -1 ? l.slice(0, sep).trim() : "";
    const texte = sep > -1 ? l.slice(sep + 2).trim() : l;
    const last = FORMATION_GROUPS[FORMATION_GROUPS.length - 1];
    if (last && last.annee === annee) last.items.push(texte);
    else FORMATION_GROUPS.push({ annee, items: [texte] });
  });

  return (
    <Page testId="eugenie-page">
      <section className="relative overflow-hidden mx-auto max-w-7xl px-6 pt-36 md:px-12 md:pt-48">
        <div className="pointer-events-none absolute -top-10 right-[-8rem] h-[22rem] w-[22rem] rounded-full bg-sand/40 blur-3xl" aria-hidden="true" />
        <FadeUp><Meta>{c("eugenie.hero.meta")}</Meta></FadeUp>
        <h1 className="mt-6 font-serif text-5xl font-light leading-[1.08] tracking-tight md:text-7xl">
          <MaskedLine delay={0.2}>{c("eugenie.hero.t1")} <em className="text-clay">{c("eugenie.hero.t2")}</em></MaskedLine>
        </h1>
        <FadeUp delay={0.5} className="mt-10 max-w-2xl">
          <p className="text-base leading-relaxed text-smoke md:text-lg">
            {c("eugenie.hero.p")}
          </p>
        </FadeUp>
      </section>

      <section className="mx-auto grid max-w-7xl items-center gap-14 px-6 py-24 md:px-12 md:py-36 lg:grid-cols-12">
        <FadeUp className="lg:col-span-5" y={40}>
          <div className="relative mx-auto max-w-sm">
            <div className={`absolute -left-6 -top-6 h-full w-full border border-clay/40 ${ARCH}`} aria-hidden="true" />
            <ParallaxImage src={c("eugenie.intro.img")} alt="Portrait d'Eugénie Larrivée" className={`relative aspect-[4/5] ${ARCH}`} speed={6} position="50% 18%" />
          </div>
        </FadeUp>
        <div className="lg:col-span-7">
          <FadeUp>
            <p className="font-hand text-3xl text-clay md:text-4xl">{c("eugenie.intro.hand")}</p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-smoke md:text-lg">
              {c("eugenie.intro.p1")}
            </p>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-smoke md:text-lg">
              {c("eugenie.intro.p2")}
            </p>
          </FadeUp>
        </div>
      </section>

      {/* LE RÉCIT */}
      <section data-testid="eugenie-chapters-section" className="bg-shell py-24 md:py-36">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <FadeUp><Meta>{c("eugenie.recit.meta")}</Meta></FadeUp>
          <div className="mt-14 space-y-20 md:space-y-28">
            {CHAPTERS.map((ch, i) => (
              <div key={ch.n} data-testid={`eugenie-chapter-${ch.n}`}
                className={`grid items-start gap-6 md:grid-cols-12 ${i % 2 === 1 ? "md:text-right" : ""}`}>
                <FadeUp className={`md:col-span-4 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                  <span className="font-serif text-6xl font-light text-clay/30 md:text-8xl">{ch.n}</span>
                </FadeUp>
                <FadeUp delay={0.12} className={`md:col-span-8 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                  <h2 className="font-serif text-3xl font-light tracking-tight md:text-4xl">{ch.title}</h2>
                  <p className={`mt-5 max-w-xl text-base leading-relaxed text-smoke md:text-lg ${i % 2 === 1 ? "md:ml-auto" : ""}`}>
                    {ch.text}
                  </p>
                </FadeUp>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION · VISION · PROMESSE */}
      <section data-testid="eugenie-foundations-section" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-36">
        <FadeUp><Meta>{c("eugenie.fondations.meta")}</Meta></FadeUp>
        <div className="mt-14 space-y-0">
          {FOUNDATIONS.map((f, i) => (
            <FadeUp key={f.label} delay={0.08 * i}>
              <div data-testid={`eugenie-foundation-${i + 1}`}
                className="grid gap-4 border-b border-linen py-12 md:grid-cols-12 md:gap-10">
                <h2 className="font-serif text-2xl font-light italic text-clay md:col-span-3 md:text-3xl">{f.label}</h2>
                <p className="max-w-2xl font-serif text-xl font-light leading-relaxed text-charcoal md:col-span-9 md:text-2xl">
                  {f.text}
                </p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* LES PILIERS */}
      <section data-testid="eugenie-pillars-section" className="bg-sage/40 py-24 md:py-36">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <FadeUp><Meta>{c("eugenie.piliers.meta")}</Meta></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 max-w-3xl font-serif text-4xl font-light leading-[1.12] tracking-tight md:text-5xl">
              {c("eugenie.piliers.t1")}<em className="text-clay">{c("eugenie.piliers.t2")}</em>
            </h2>
          </FadeUp>
          <div className="mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {PILLARS.map((p, i) => (
              <FadeUp key={p.title} delay={0.07 * i}>
                <div data-testid={`pillar-${p.title.toLowerCase()}`} className="group border-t border-charcoal/10 pt-8">
                  <p.Icon className={`h-7 w-7 transition-transform duration-500 group-hover:-translate-y-1 ${i % 2 === 1 ? "text-moss" : "text-clay"}`} strokeWidth={1.25} />
                  <h3 className="mt-5 font-serif text-2xl font-light tracking-tight">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-smoke md:text-base">{p.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* BANDEAU MOODBOARD : retiré à la demande */}
      <section data-testid="eugenie-parcours-section" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-36">
        <FadeUp><Meta>{c("eugenie.parcours.meta")}</Meta></FadeUp>
        <div className="mt-12">
          {PARCOURS.map((m, i) => (
            <FadeUp key={m.period} delay={0.06 * i}>
              <div data-testid={`eugenie-milestone-${i + 1}`}
                className="grid gap-3 border-b border-linen py-9 transition-colors duration-300 hover:bg-shell/70 md:grid-cols-12 md:gap-8 md:px-6 md:rounded-[1.75rem]">
                <p className="font-serif text-2xl font-light italic text-clay md:col-span-4">{m.period}</p>
                <p className="text-base leading-relaxed text-smoke md:col-span-8 md:text-lg">{m.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section data-testid="eugenie-diplomes-section" className="bg-shell py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <FadeUp><Meta>{c("eugenie.diplomes.meta")}</Meta></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 max-w-2xl font-serif text-3xl font-light leading-snug tracking-tight md:text-4xl">
              {c("eugenie.diplomes.t1")} <em className="text-clay">{c("eugenie.diplomes.t2")}</em>
            </h2>
          </FadeUp>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {DIPLOMES.map((d, i) => (
              <FadeUp key={d} delay={0.06 * i}>
                <div data-testid={`eugenie-diploma-${i + 1}`} className="flex items-start gap-4 border-t border-linen pt-6">
                  <span className="font-serif text-lg italic text-clay/70">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-sm leading-relaxed text-smoke md:text-base">{d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2}>
            <p className="mt-8 text-xs italic text-ash">{c("eugenie.diplomes.note")}</p>
          </FadeUp>
        </div>
      </section>

      {/* FORMATION CONTINUE */}
      <section data-testid="eugenie-formations-section" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-36">
        <FadeUp><Meta>{c("eugenie.formations.meta")}</Meta></FadeUp>
        <FadeUp delay={0.1}>
          <h2 className="mt-6 max-w-2xl font-serif text-3xl font-light leading-snug tracking-tight md:text-4xl">
            {c("eugenie.formations.t1")} <em className="text-clay">{c("eugenie.formations.t2")}</em>
          </h2>
        </FadeUp>
        <div className="mt-14 space-y-16">
          {FORMATION_GROUPS.map((g, gi) => (
            <FadeUp key={`${g.annee}-${gi}`} delay={0.05 * gi}>
              <div data-testid={`eugenie-formation-group-${gi + 1}`} className="grid gap-6 md:grid-cols-12 md:gap-10">
                <p className="font-serif text-5xl font-light text-clay/70 md:col-span-3">{g.annee}</p>
                <ul className="space-y-5 md:col-span-9">
                  {g.items.map((t, i) => (
                    <li key={i} data-testid={`eugenie-formation-${gi + 1}-${i + 1}`}
                      className="border-t border-linen pt-5 text-sm leading-relaxed text-smoke md:text-base">
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 text-center md:px-12 md:py-32">
        <FadeUp>
          <p className="font-hand text-3xl text-clay md:text-4xl">{c("eugenie.fin.hand")}</p>
        </FadeUp>
        <FadeUp delay={0.15}>
          <Link to="/conversations" data-testid="eugenie-cta-conversations"
            className="group mt-10 inline-flex items-center gap-2 rounded-full bg-clay px-8 py-4 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal">
            {c("eugenie.fin.cta")}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </section>
    </Page>
  );
}
